import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Ban,
  Film,
  Image as ImageIcon,
  LayoutGrid,
  Layers,
  List,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';

import { useAssetQuery, type AssetListParams } from '@/client/api/assets/assets.query';
import {
  useBulkAssetMutation,
  useDeleteAssetMutation,
  useRestoreAssetMutation,
} from '@/client/api/assets/assets.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

import AssetCard, { assetState } from '@/client/components/admin/Assets/AssetCard';
import AssetImage from '@/client/components/admin/Assets/AssetImage';
import { AssetDetailsDrawer } from '@/client/components/admin/Assets/AssetDetailsDrawer';
import MatchStolenDialog from '@/client/components/admin/Assets/MatchStolenDialog';
import JobLauncherDialog from '@/client/components/admin/Assets/JobLauncherDialog';
import UploadForSellerDialog from '@/client/components/admin/Assets/UploadForSellerDialog';
import { useExportCsv } from '@/client/api/assets/actions';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { filterScope, type JobScope } from '@/client/api/assets/jobs';
import StatsGrid, { Stat } from '@/client/components/common/StatsGrid';
import Pagination from '@/client/components/common/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const FILTER_DEFAULTS = {
  type: '',
  state: '',
  role: '',
  sort: 'newest',
  uid: '',
  oid: '',
  has_phash: '',
  date_from: '',
  date_to: '',
  signal: '',
  moderation: '',
};

const TYPE_OPTIONS = [
  ['', 'All media'],
  ['image', 'Images'],
  ['video', 'Videos'],
  ['3d', '3D & tours'],
  ['document', 'Documents'],
  ['audio', 'Audio'],
];
const STATE_OPTIONS = [
  ['', 'All states'],
  ['active', 'Active'],
  ['inactive', 'Inactive'],
  ['deleted', 'Deleted'],
  ['private', 'Private'],
  ['quarantined', 'Quarantined'],
];
const ROLE_OPTIONS = [
  ['', 'Any role'],
  ['cover', 'Cover'],
  ['featured', 'Featured'],
  ['logo', 'Logo'],
  ['floor_plan', 'Floor plan'],
  ['360', '360°'],
  ['tour', 'Virtual tour'],
];
const MODERATION_OPTIONS = [
  ['', 'Any review'],
  ['hidden', 'Hidden'],
  ['rejected', 'Rejected'],
  ['approved', 'Approved'],
  ['flagged', 'Flagged'],
];
const SORT_OPTIONS = [
  ['newest', 'Newest first'],
  ['oldest', 'Oldest first'],
  ['name', 'Name A–Z'],
];

const PAGE_SIZES = [24, 48, 96];

const formatBytes = (n?: number | null) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 ** 2).toFixed(1)} MB`;
};

const NativeSelect = ({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[][];
  label: string;
}) => (
  <select
    aria-label={label}
    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {options.map(([v, l]) => (
      <option key={v} value={v}>
        {l}
      </option>
    ))}
  </select>
);

const GridSkeleton = ({ count }: { count: number }) => (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
    {Array.from({ length: Math.min(count, 24) }).map((_, i) => (
      <div key={i} className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <Skeleton className="aspect-square w-full" />
        <div className="space-y-2 p-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const AssetsPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '#' }, { label: 'Library' }]);

  // Search, filters, page and page size all live in the URL, so a filtered view
  // can be shared and survives a reload.
  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters({ initialFilters: FILTER_DEFAULTS, initialLimit: 24 });

  const params: AssetListParams = {
    page_size: limit,
    q: debouncedSearchTerm,
    type: filters.type,
    state: filters.state,
    role: filters.role,
    sort: filters.sort,
    uid: filters.uid,
    oid: filters.oid,
    has_phash: filters.has_phash,
    date_from: filters.date_from,
    date_to: filters.date_to,
    signal: filters.signal,
    moderation: filters.moderation,
    facets: 1,
  };
  const { data, isLoading, isError, error, isFetching, refetch } = useAssetQuery(page, params);
  const assets = useMemo<Asset[]>(() => data?.assets ?? [], [data]);
  const total = data?.total ?? 0;
  const facets = data?.facets;

  const moreActive = Boolean(
    filters.uid || filters.oid || filters.has_phash || filters.date_from || filters.date_to,
  );
  const [showMore, setShowMore] = useState(moreActive);
  const [view, setView] = useState<'grid' | 'table'>('grid');
  const anyFilter =
    Boolean(debouncedSearchTerm || filters.type || filters.state || filters.role || filters.signal || filters.moderation) || moreActive;

  // Tiles count everything matching the filters (server facets), not one page.
  const stats: Stat[] = [
    { key: 'total', label: 'Matching', value: total, hint: anyFilter ? 'with these filters' : 'all assets', icon: Layers, tone: 'bg-blue-50 text-blue-600' },
    { key: 'images', label: 'Images', value: facets?.kind.image ?? 0, icon: ImageIcon, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'videos', label: 'Videos', value: facets?.kind.video ?? 0, icon: Film, tone: 'bg-purple-50 text-purple-600' },
    { key: 'deleted', label: 'Deleted', value: facets?.state.deleted ?? 0, icon: Trash2, tone: 'bg-red-50 text-red-600' },
    { key: 'inactive', label: 'Inactive', value: facets?.state.inactive ?? 0, hint: 'not deleted, not live', icon: Ban, tone: 'bg-amber-50 text-amber-600' },
  ];

  // Selection is per page: it clears whenever the page or the filters change.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const queryKey = JSON.stringify([page, params]);
  useEffect(() => setSelected(new Set()), [queryKey]);
  const toggle = (asid: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(asid)) next.delete(asid);
      else next.add(asid);
      return next;
    });
  const pageAsids = assets.map((a) => a.asid).filter((x): x is number => x != null);
  const allSelected = pageAsids.length > 0 && pageAsids.every((a) => selected.has(a));
  const selectedRows = assets.filter((a) => a.asid != null && selected.has(a.asid));
  const canDelete = selectedRows.filter((a) => a.deleted !== 1).map((a) => a.asid!);
  const canRestore = selectedRows.filter((a) => a.deleted === 1).map((a) => a.asid!);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Asset | null>(null);
  const [pendingBulk, setPendingBulk] = useState<'delete' | 'restore' | null>(null);
  const [matchOpen, setMatchOpen] = useState(false);
  const [jobScope, setJobScope] = useState<{ scope: JobScope; label: string } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportCsv = useExportCsv();
  const { hasPermission: canUpdate, isSuperAdmin: isSuper } = useHasPermission('asset.update');
  const [matchUrl, setMatchUrl] = useState<string | undefined>(undefined);
  const [mutatingAsid, setMutatingAsid] = useState<number | null>(null);

  const deleteMutation = useDeleteAssetMutation();
  const restoreMutation = useRestoreAssetMutation();
  const bulkMutation = useBulkAssetMutation();

  const confirmDelete = async () => {
    const asid = pendingDelete?.asid;
    setPendingDelete(null);
    if (asid == null) return;
    setMutatingAsid(asid);
    try {
      await deleteMutation.mutateAsync({ asid });
      toast.success(`Deleted asset #${asid}`);
      if (selectedAsset?.asid === asid) setSelectedAsset(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete the asset'));
    } finally {
      setMutatingAsid(null);
    }
  };

  const handleRestore = async (asset: Asset) => {
    const asid = asset.asid;
    if (asid == null) return;
    setMutatingAsid(asid);
    try {
      await restoreMutation.mutateAsync({ asid });
      toast.success(`Restored asset #${asid}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to restore the asset'));
    } finally {
      setMutatingAsid(null);
    }
  };

  const confirmBulk = async () => {
    const action = pendingBulk;
    setPendingBulk(null);
    if (!action) return;
    const asids = action === 'delete' ? canDelete : canRestore;
    const { done, failed } = await bulkMutation.mutateAsync({ action, asids });
    const verb = action === 'delete' ? 'Deleted' : 'Restored';
    if (failed.length) toast.error(`${verb} ${done}; failed: #${failed.join(', #')}`);
    else toast.success(`${verb} ${done} asset${done === 1 ? '' : 's'}`);
    setSelected(new Set());
  };

  const openMatchFor = (asset: Asset) => {
    setMatchUrl(asset.display_url || asset.public_url || undefined);
    setMatchOpen(true);
  };

  const bulkCount = pendingBulk === 'delete' ? canDelete.length : canRestore.length;

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Asset library</h1>
            <p className="mt-1 text-sm text-gray-500">
              Every photo, video and file on the platform. Search, filter, and remove or restore media.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(canUpdate || isSuper) && (
              <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>Upload for a seller</Button>
            )}
            <Button variant="outline" size="sm" disabled={exporting} onClick={async () => {
              setExporting(true);
              try {
                const n = await exportCsv(params as Record<string, unknown>);
                toast.success(`Exported ${n.toLocaleString()} rows${n >= 20000 ? ' (first 20,000; narrow the filter for more)' : ''}`);
              } catch (err) {
                toast.error(getApiErrorMessage(err, 'Export failed'));
              } finally {
                setExporting(false);
              }
            }}>{exporting ? 'Exporting…' : 'Export CSV'}</Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setMatchUrl(undefined);
                setMatchOpen(true);
              }}
            >
              <ScanSearch className="mr-2 h-4 w-4" />
              Reverse-image lookup
            </Button>
          </div>
        </div>

        <StatsGrid
          stats={stats}
          isLoading={isLoading && !data}
          error={isError ? 'Could not load assets.' : null}
          className="lg:grid-cols-5 xl:grid-cols-5"
        />

        {/* Toolbar: every control is a server-side filter */}
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search by asset or listing id, uuid, or file name"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <NativeSelect label="Media type" value={filters.type} onChange={(v) => handleFilterChange('type', v)} options={TYPE_OPTIONS} />
            <NativeSelect label="State" value={filters.state} onChange={(v) => handleFilterChange('state', v)} options={STATE_OPTIONS} />
            <NativeSelect label="Role" value={filters.role} onChange={(v) => handleFilterChange('role', v)} options={ROLE_OPTIONS} />
            <NativeSelect label="Moderation" value={filters.moderation} onChange={(v) => handleFilterChange('moderation', v)} options={MODERATION_OPTIONS} />
            <NativeSelect label="Sort" value={filters.sort} onChange={(v) => handleFilterChange('sort', v)} options={SORT_OPTIONS} />
            <Button variant={showMore ? 'secondary' : 'outline'} size="sm" onClick={() => setShowMore((s) => !s)}>
              <SlidersHorizontal className="mr-2 h-4 w-4" /> More
            </Button>
            <div className="ml-auto flex items-center gap-1 rounded-md border p-0.5">
              <button
                aria-label="Grid view"
                className={cn('rounded p-1.5', view === 'grid' ? 'bg-gray-100' : 'text-gray-400')}
                onClick={() => setView('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                aria-label="Table view"
                className={cn('rounded p-1.5', view === 'table' ? 'bg-gray-100' : 'text-gray-400')}
                onClick={() => setView('table')}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {showMore && (
            <div className="flex flex-wrap items-end gap-3 border-t pt-3 text-sm">
              <label className="space-y-1">
                <span className="block text-xs text-gray-500">Uploader user id</span>
                <Input className="w-32" inputMode="numeric" value={filters.uid}
                  onChange={(e) => handleFilterChange('uid', e.target.value.replace(/\D/g, ''))} />
              </label>
              <label className="space-y-1">
                <span className="block text-xs text-gray-500">Organization id</span>
                <Input className="w-32" inputMode="numeric" value={filters.oid}
                  onChange={(e) => handleFilterChange('oid', e.target.value.replace(/\D/g, ''))} />
              </label>
              <label className="space-y-1">
                <span className="block text-xs text-gray-500">Perceptual hash</span>
                <NativeSelect label="Perceptual hash" value={filters.has_phash}
                  onChange={(v) => handleFilterChange('has_phash', v)}
                  options={[['', 'Any'], ['1', 'Hashed'], ['0', 'Not hashed']]} />
              </label>
              <label className="space-y-1">
                <span className="block text-xs text-gray-500">Uploaded from</span>
                <Input type="date" value={filters.date_from} onChange={(e) => handleFilterChange('date_from', e.target.value)} />
              </label>
              <label className="space-y-1">
                <span className="block text-xs text-gray-500">Uploaded to</span>
                <Input type="date" value={filters.date_to} onChange={(e) => handleFilterChange('date_to', e.target.value)} />
              </label>
            </div>
          )}

          {anyFilter && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {filters.signal && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
                  Signal: {filters.signal.replace(':', ' = ').replace(/_/g, ' ')}
                  <button aria-label="Remove signal filter" onClick={() => handleFilterChange('signal', '')}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {total.toLocaleString()} matching
              {total > 0 && (
                <button className="text-blue-600 hover:underline"
                  onClick={() => setJobScope({ scope: { filter: filterScope(params as Record<string, unknown>) }, label: `all ${total.toLocaleString()} photos matching these filters` })}>
                  Run a job on all matching
                </button>
              )}
              <button className="inline-flex items-center gap-1 text-blue-600 hover:underline" onClick={resetFilters}>
                <X className="h-3 w-3" /> Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Selection bar */}
        {selected.size > 0 && (
          <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm shadow-sm">
            <span className="font-medium">{selected.size} selected on this page</span>
            <Button size="sm" variant="destructive" disabled={!canDelete.length || bulkMutation.isPending}
              onClick={() => setPendingBulk('delete')}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete {canDelete.length || ''}
            </Button>
            <Button size="sm" variant="outline" disabled={!canRestore.length || bulkMutation.isPending}
              onClick={() => setPendingBulk('restore')}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore {canRestore.length || ''}
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setJobScope({ scope: { asids: Array.from(selected) }, label: `${selected.size} selected photos` })}>
              More actions…
            </Button>
            <button className="ml-auto text-blue-700 hover:underline" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        {/* Results */}
        {isLoading && !data ? (
          <GridSkeleton count={limit} />
        ) : isError ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-12 text-center text-sm text-red-600">
            {getApiErrorMessage(error, 'Could not load assets.')}{' '}
            <button onClick={() => refetch()} className="underline">Try again</button>
          </div>
        ) : assets.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center rounded-lg border-2 border-dashed bg-white">
            <ImageIcon className="mb-3 h-10 w-10 text-gray-300" />
            <p className="text-sm text-gray-500">
              {anyFilter ? 'No assets match these filters.' : 'No assets yet.'}
            </p>
          </div>
        ) : view === 'table' ? (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input type="checkbox" aria-label="Select page" checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(pageAsids))} />
                  </th>
                  <th className="px-3 py-2">Preview</th>
                  <th className="px-3 py-2">Asset</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Size</th>
                  <th className="px-3 py-2">Uploaded</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {assets.map((a) => {
                  const st = assetState(a);
                  return (
                    <tr key={a.asid} className={cn('hover:bg-gray-50', a.deleted === 1 && 'opacity-70')}>
                      <td className="px-3 py-2">
                        <input type="checkbox" aria-label={`Select asset ${a.asid}`}
                          checked={selected.has(a.asid!)} onChange={() => toggle(a.asid!)} />
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => setSelectedAsset(a)} className="block h-12 w-12 overflow-hidden rounded">
                          <AssetImage asset={a} className="h-12 w-12 object-cover" />
                        </button>
                      </td>
                      <td className="max-w-[260px] px-3 py-2">
                        <button className="block truncate text-left font-medium hover:underline" title={a.name}
                          onClick={() => setSelectedAsset(a)}>
                          {a.name || `Asset #${a.asid}`}
                        </button>
                        <span className="text-xs text-gray-500">#{a.asid}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', st.cls)}>{st.label}</span>
                        {a.tester === 1 && <span className="ml-1 text-[11px] text-gray-400">test</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {a.eid ? <div>Listing #{a.eid}</div> : null}
                        {a.uid ? <div>User #{a.uid}</div> : null}
                        {a.oid ? <div>Org #{a.oid}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{a.mime_type || a.kind || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-600">{formatBytes(a.size_bytes)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {a.date_added ? new Date(a.date_added).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {a.deleted === 1 ? (
                          <Button size="sm" variant="ghost" disabled={mutatingAsid === a.asid} onClick={() => handleRestore(a)}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" disabled={mutatingAsid === a.asid} onClick={() => setPendingDelete(a)}
                            aria-label={`Delete asset ${a.asid}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <label className="flex w-fit items-center gap-2 text-xs text-gray-500">
              <input type="checkbox" checked={allSelected}
                onChange={() => setSelected(allSelected ? new Set() : new Set(pageAsids))} />
              Select all on this page
            </label>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {assets.map((asset) => (
                <AssetCard
                  key={asset.asid}
                  asset={asset}
                  onView={setSelectedAsset}
                  onDelete={setPendingDelete}
                  onRestore={handleRestore}
                  pending={mutatingAsid === asset.asid}
                  selected={selected.has(asset.asid!)}
                  onToggleSelect={() => toggle(asset.asid!)}
                />
              ))}
            </div>
          </>
        )}

        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow sm:px-6">
          <Pagination
            total={total}
            page={page}
            itemsPerPage={limit}
            limit={limit}
            setPage={handlePageChange}
            setItemsPerPage={handleLimitChange}
            showButtons
            showSelect
            selectOptions={PAGE_SIZES}
          />
        </div>
      </div>

      <AssetDetailsDrawer
        // The list refetches after every decision; show the fresh row, not the click-time snapshot.
        asset={assets.find((a) => a.asid === selectedAsset?.asid) ?? selectedAsset}
        isOpen={!!selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onDelete={setPendingDelete}
        onRestore={handleRestore}
        onFindSimilar={openMatchFor}
        pending={selectedAsset ? mutatingAsid === selectedAsset.asid : false}
      />

      <UploadForSellerDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      <JobLauncherDialog open={!!jobScope} onClose={() => setJobScope(null)} scope={jobScope?.scope ?? null} scopeLabel={jobScope?.label ?? ''} />

      <MatchStolenDialog open={matchOpen} onClose={() => setMatchOpen(false)} initialUrl={matchUrl} />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              Asset #{pendingDelete?.asid} will be soft-deleted and hidden from its listing. If it was the
              cover, the listing's next photo becomes the cover. You can restore it from the Deleted state.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingBulk} onOpenChange={(o) => !o && setPendingBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulk === 'delete' ? 'Delete' : 'Restore'} {bulkCount} asset{bulkCount === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulk === 'delete'
                ? 'They will be soft-deleted and hidden from their listings. Covers pass to the next photo. You can restore them later.'
                : 'They will be reactivated on their listings.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={pendingBulk === 'delete' ? 'bg-red-600 hover:bg-red-700' : undefined}
              onClick={(e) => {
                e.preventDefault();
                void confirmBulk();
              }}
            >
              {pendingBulk === 'delete' ? 'Delete' : 'Restore'} {bulkCount}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetsPage;
